/**
 * Afrikaans: Bookings
 *
 * What this file covers: The Bookings menu: calendar, bookings, services, staff and hours, classes, booking
 * settings, calendar sync.
 *
 * Tone: Friendly and clear, for small business owners. Times and dates read the way the language says them.
 * Words the client sees (confirmations, reminders) sound polite and personal.
 *
 * Afrikaans: Standard South African Afrikaans. Address the reader as "jy". Use the same Afrikaans word for the
 * same English term everywhere; menu.ts and common.ts set the words the other files follow. Afrikaans must stay
 * complete: the release checks fail if a key is missing.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const bookings: Catalog = {
  'bookings.title': 'Besprekings',
  'bookings.readOnly': 'Net eienaars en administrateurs kan dit verander. Jy kan alles sien en besprekings maak, skuif en kanselleer.',
  'bookings.reasonHint': 'Opsioneel. Die kliënt sien dit wanneer hulle laat weet word.',
  'bookings.discard.title': 'Gooi jou veranderinge weg?',
  'bookings.discard.body': 'Wat jy hier verander het, is nie gestoor nie.',
  'bookings.limit.seePlans': 'Sien planne',
  'bookings.colour.option': 'Kleur {{n}}',
  'bookings.error.SLOT_TAKEN': 'Daardie tyd is pas geneem. Kies \'n ander een.',
  'bookings.error.FULL': 'Daar is geen plekke oor nie. Kies \'n ander tyd.',
  'bookings.error.NOT_BOOKABLE': 'Daardie tyd kan nie bespreek word nie: dit is buite die ure, te gou of te ver vorentoe, of die diens is afgeskakel.',
  'bookings.error.TOO_LATE': 'Dit is te naby aan die begin om hierdie bespreking te verander.',
  'bookings.error.PLAN_LIMIT_REACHED': 'Jou plan laat nie meer toe nie. Gradeer jou plan op of skakel eers een af.',
  'bookings.error.forbidden': 'Net eienaars en administrateurs kan dit doen.',
  'bookings.error.unavailable': 'Besprekings is nou nie beskikbaar nie. Probeer oor \'n minuut weer.',
  'bookings.status.requested': 'Wag vir goedkeuring',
  'bookings.status.confirmed': 'Bevestig',
  'bookings.status.declined': 'Afgewys',
  'bookings.status.cancelled': 'Gekanselleer',
  'bookings.status.completed': 'Voltooi',
  'bookings.status.no_show': 'Het nie opgedaag nie',
  'bookings.channel.console': 'Hier bespreek',
  'bookings.channel.whatsapp': 'WhatsApp',
  'bookings.channel.web': 'Webwerfklets',
  'bookings.channel.email': 'E-pos',
  'bookings.channel.sms': 'SMS',
  'bookings.channel.messenger': 'Messenger',
  'bookings.channel.instagram': 'Instagram',
  'bookings.channel.voice': 'Telefoonoproep',
  'bookings.channel.ussd': 'USSD',
  'bookings.channel.public': 'Besprekingsblad',
  'bookings.source.console': '\'n Spanlid',
  'bookings.source.flow': 'Die kletsbot',
  'bookings.source.voice': 'Die telefoonassistent',
  'bookings.source.inbox': 'Die inkassie',
  'bookings.source.tickets': '\'n Kaartjie',
  'bookings.source.public': 'Die besprekingsblad',
  'bookings.history.created': 'Bespreek',
  'bookings.history.requested': 'Aangevra',
  'bookings.history.confirmed': 'Bevestig',
  'bookings.history.approved': 'Goedgekeur',
  'bookings.history.declined': 'Afgewys',
  'bookings.history.moved': 'Geskuif',
  'bookings.history.cancelled': 'Gekanselleer',
  'bookings.history.completed': 'Voltooi',
  'bookings.history.no_show': 'Gemerk as het nie opgedaag nie',
  'bookings.history.note': 'Nota bygevoeg',
  'bookings.history.reminder_sent': 'Herinnering gestuur',
  'bookings.history.client_confirmed': 'Kliënt het bevestig',
  'bookings.kind.appointment': 'Afspraak',
  'bookings.kind.class': 'Klas of geleentheid',
  'bookings.kind.callout': 'Huisbesoek',
  'bookings.location.business': 'By die besigheid',
  'bookings.location.client': 'By die kliënt se adres',
  'bookings.location.online': 'Aanlyn',
  'bookings.questionType.text': 'Kort antwoord',
  'bookings.questionType.number': 'Getal',
  'bookings.questionType.date': 'Datum',
  'bookings.questionType.select': 'Kies uit \'n lys',
  'bookings.questionType.yes_no': 'Ja of nee',
  'bookings.questionType.address': 'Adres',
  'bookings.field.number': 'Besprekingsnommer',
  'bookings.field.when': 'Wanneer',
  'bookings.field.client': 'Kliënt',
  'bookings.field.clientName': 'Kliënt se naam',
  'bookings.field.phone': 'Telefoon',
  'bookings.field.service': 'Diens',
  'bookings.field.person': 'Persoon',
  'bookings.field.address': 'Adres',
  'bookings.field.answers': 'Antwoorde',
  'bookings.field.notes': 'Notas',
  'bookings.field.channel': 'Kanaal',
  'bookings.field.source': 'Bespreek deur',
  'bookings.field.reason': 'Rede',
  'bookings.field.notifyClient': 'Laat die kliënt weet',
  'bookings.action.approve': 'Keur goed',
  'bookings.action.decline': 'Wys af',
  'bookings.action.move': 'Skuif',
  'bookings.action.cancel': 'Kanselleer bespreking',
  'bookings.action.complete': 'Merk as voltooi',
  'bookings.action.noShow': 'Het nie opgedaag nie',
  'bookings.toast.approved': '{{number}} is goedgekeur en die kliënt is laat weet.',
  'bookings.toast.declined': '{{number}} is afgewys.',
  'bookings.toast.moved': '{{number}} is geskuif.',
  'bookings.toast.cancelled': '{{number}} is gekanselleer.',
  'bookings.toast.completed': '{{number}} is as voltooi gemerk.',
  'bookings.toast.noShow': '{{number}} is gemerk as het nie opgedaag nie.',
  'bookings.toast.noteAdded': 'Nota by {{number}} gevoeg.',
  'bookings.cancel.title': 'Kanselleer {{number}}?',
  'bookings.cancel.body': '{{name}} se bespreking op {{when}} word gekanselleer en die tyd vrygestel.',
  'bookings.cancel.confirm': 'Kanselleer bespreking',
  'bookings.decline.title': 'Wys {{number}} af?',
  'bookings.decline.body': '{{name}} het {{when}} aangevra. Om af te wys, laat hulle weet dit kon nie bespreek word nie.',
  'bookings.decline.confirm': 'Wys af',
  'bookings.move.title': 'Skuif {{number}}',
  'bookings.move.now': 'Tans bespreek vir {{when}}.',
  'bookings.move.notifyHint': 'Stuur die nuwe tyd op die kanaal waarvandaan die bespreking gekom het, en per e-pos as daar \'n adres is.',
  'bookings.move.confirm': 'Skuif na hierdie tyd',
  'bookings.drawer.title': 'Bespreking {{number}}',
  'bookings.drawer.loading': 'Bespreking',
  'bookings.drawer.when': '{{start}} tot {{end}}',
  'bookings.drawer.with': 'Met {{name}}',
  'bookings.drawer.openConversation': 'Maak die gesprek oop',
  'bookings.drawer.openContact': 'Maak die kontak oop',
  'bookings.drawer.addNote': 'Voeg \'n nota by',
  'bookings.drawer.addNoteHint': 'Net vir jou span. Die kliënt sien nooit notas nie.',
  'bookings.drawer.saveNote': 'Stoor nota',
  'bookings.drawer.history': 'Geskiedenis',
  'bookings.drawer.noHistory': 'Niks het nog met hierdie bespreking gebeur nie.',
  'bookings.picker.day': 'Dag',
  'bookings.picker.time': 'Tyd',
  'bookings.picker.session': 'Sessie',
  'bookings.picker.windowLabel': 'Aankomsvenster',
  'bookings.picker.window': '{{from}} tot {{to}}',
  'bookings.picker.placesLeft': '{{count}} oor',
  'bookings.picker.earlier': 'Vroeër',
  'bookings.picker.later': 'Later',
  'bookings.picker.noneThisWeek': 'Niks is vry op hierdie dae nie. Probeer later dae.',
  'bookings.picker.noneThisDay': 'Niks is vry op hierdie dag nie. Kies \'n ander een.',
  'bookings.picker.chosen': 'Gekies: {{when}}',
  'bookings.new.title': 'Nuwe bespreking',
  'bookings.new.chooseService': 'Kies \'n diens',
  'bookings.new.serviceRequired': 'Kies die diens wat bespreek word.',
  'bookings.new.anyone': 'Wie ook al vry is',
  'bookings.new.personHint': 'Laat dit op wie ook al vry is om elke vrye tyd te sien.',
  'bookings.new.timeRequired': 'Kies \'n tyd.',
  'bookings.new.findContact': 'Soek \'n kontak',
  'bookings.new.findContactHint': 'Tik \'n naam, nommer of e-posadres om die kliënt uit jou kontakte in te vul.',
  'bookings.new.nameRequired': 'Voer die kliënt se naam in.',
  'bookings.new.phoneHint': 'Met die landkode, soos +27 82 123 4567.',
  'bookings.new.reachRequired': 'Voer \'n telefoonnommer of \'n e-posadres in, sodat die kliënt laat weet kan word.',
  'bookings.new.addressRequired': 'Voer die adres in waarheen gegaan moet word.',
  'bookings.new.answerRequired': 'Hierdie vraag moet beantwoord word.',
  'bookings.new.notesHint': 'Net vir jou span.',
  'bookings.new.notifyHint': 'Stuur nou die bevestiging, en herinneringe voor die tyd.',
  'bookings.new.submit': 'Bespreek',
  'bookings.new.noServices': 'Daar is nog geen dienste om te bespreek nie.',
  'bookings.new.doneTitle': 'Bespreek',
  'bookings.new.doneConfirmed': 'Die bespreking is bevestig.',
  'bookings.new.doneRequested': 'Die bespreking wag vir goedkeuring.',
  'bookings.new.openBooking': 'Maak die bespreking oop',
  'bookings.calendar.title': 'Kalender',
  'bookings.calendar.description': 'Elke bespreking volgens dag en tyd. Klik op \'n leë tyd om dit te bespreek.',
  'bookings.calendar.today': 'Vandag',
  'bookings.calendar.previous': 'Vroeër',
  'bookings.calendar.next': 'Later',
  'bookings.calendar.week': 'Week',
  'bookings.calendar.day': 'Dag',
  'bookings.calendar.weekOf': '{{from}} tot {{to}}',
  'bookings.calendar.everyone': 'Almal',
  'bookings.calendar.showCancelled': 'Wys gekanselleerdes',
  'bookings.calendar.clickToBook': 'Klik om hierdie tyd te bespreek',
  'bookings.calendar.blockLabel': '{{time}}, {{service}}, {{client}}, {{status}}',
  'bookings.calendar.zone': 'Tye is in {{zone}}.',
  'bookings.calendar.empty': 'Geen besprekings op hierdie dae nie.',
  'bookings.list.title': 'Besprekings',
  'bookings.list.description': 'Elke bespreking, met filters om een te vind. Keur versoeke van hier af goed of wys hulle af.',
  'bookings.list.search': 'Soek volgens naam, nommer of besprekingsnommer',
  'bookings.list.anyStatus': 'Enige status',
  'bookings.list.anyService': 'Enige diens',
  'bookings.list.from': 'Van',
  'bookings.list.to': 'Tot',
  'bookings.list.empty': 'Nog geen besprekings nie',
  'bookings.list.emptyHint': 'Besprekings wat hier, deur die kletsbot of op jou besprekingsblad gemaak word, verskyn in hierdie lys.',
  'bookings.list.emptyFiltered': 'Geen besprekings pas by hierdie filters nie',
  'bookings.list.emptyFilteredHint': 'Maak die filters skoon om elke bespreking van vandag af te sien.',
  'bookings.list.approveFor': 'Keur {{number}} goed',
  'bookings.list.declineFor': 'Wys {{number}} af',
  'bookings.services.title': 'Dienste',
  'bookings.services.description': 'Wat kliënte kan bespreek: afsprake met \'n persoon, klasse en geleenthede, en huisbesoeke by hul adres.',
  'bookings.services.new': 'Voeg \'n diens by',
  'bookings.services.addFirst': 'Voeg jou eerste diens by',
  'bookings.services.create': 'Voeg diens by',
  'bookings.services.emptyTitle': 'Nog geen dienste nie',
  'bookings.services.emptyHint': 'Voeg by wat kliënte bespreek, soos \'n haarsny, \'n joga-klas of \'n loodgieter se huisbesoek.',
  'bookings.services.noneActive': 'Geen dienste is aangeskakel nie',
  'bookings.services.noneActiveHint': 'Wys die afgeskakelde dienste om een weer aan te skakel.',
  'bookings.services.showInactive': 'Wys afgeskakeldes',
  'bookings.services.countOfLimit': '{{count}} van {{limit}}',
  'bookings.services.limitReached': 'Jou plan laat {{limit}} dienste toe. Skakel een af of gradeer jou plan op om nog een by te voeg.',
  'bookings.services.on': 'Aan',
  'bookings.services.off': 'Af',
  'bookings.services.nobody': 'Niemand doen dit nog nie',
  'bookings.services.minutes': '{{n}} min',
  'bookings.services.windowsCount': '{{count}} vensters',
  'bookings.services.remove.title': 'Verwyder {{name}}?',
  'bookings.services.remove.body': 'Kliënte kan dit nie meer bespreek nie. Besprekings wat reeds gemaak is, bly soos hulle is.',
  'bookings.services.toast.created': '{{name}} is bygevoeg.',
  'bookings.services.toast.saved': '{{name}} is gestoor.',
  'bookings.services.toast.removed': '{{name}} is verwyder.',
  'bookings.services.descriptionHint': 'Opsioneel. Kliënte sien dit wanneer hulle \'n diens kies.',
  'bookings.services.kind': 'Soort',
  'bookings.services.kindHint.appointment': '’n Vaste tydsduur, met een van jou mense of met niemand in die besonder nie, soos ’n konsultasie of ’n motorwas.',
  'bookings.services.kindHint.class': 'Baie kliënte op \'n vaste tyd, soos \'n klas, \'n werkswinkel of \'n toer.',
  'bookings.services.kindHint.callout': 'Jy gaan na die kliënt binne \'n aankomsvenster, soos \'n herstelwerk of \'n aflewering.',
  'bookings.services.kindLocked': 'Die soort kan nie verander word nadat die diens gemaak is nie.',
  'bookings.services.duration': 'Lengte',
  'bookings.services.minutesHint': 'In minute.',
  'bookings.services.bufferBefore': 'Tyd voor',
  'bookings.services.bufferAfter': 'Tyd na',
  'bookings.services.bufferHint': 'Minute wat rondom elke bespreking vry gehou word, om voor te berei of op te ruim.',
  'bookings.services.capacity': 'Plekke',
  'bookings.services.capacityHint': 'Hoeveel kliënte in een sessie pas. Elke sessie kan dit verander.',
  'bookings.services.windows': 'Aankomsvensters',
  'bookings.services.windowFrom': 'Van',
  'bookings.services.windowTo': 'Tot',
  'bookings.services.windowCapacity': 'Besoeke',
  'bookings.services.windowsHint': 'Elke werksdag bied hierdie vensters aan. Besoeke is hoeveel besoeke in een venster pas.',
  'bookings.services.addWindow': 'Voeg \'n venster by',
  'bookings.services.removeWindow': 'Verwyder venster {{n}}',
  'bookings.services.location': 'Waar',
  'bookings.services.locationHint': 'Kliënte word dit in die bevestiging vertel.',
  'bookings.services.onlineLink': 'Vergaderskakel',
  'bookings.services.onlineLinkHint': 'Word saam met die bevestiging aan die kliënt gestuur. Begin met https://.',
  'bookings.services.price': 'Prys',
  'bookings.services.priceHint': 'Opsioneel. Gewys soos jy dit skryf, soos R350 of Vanaf R200. Niks word gehef nie.',
  'bookings.services.colour': 'Kleur op die kalender',
  'bookings.services.approval': 'Goedkeuring',
  'bookings.services.approval.default': 'Gebruik die verstek',
  'bookings.services.approval.always': 'Vra altyd eers',
  'bookings.services.approval.never': 'Bevestig dadelik',
  'bookings.services.approvalHint': 'As jy eers vra, wag \'n bespreking totdat iemand dit goedkeur. Die verstek word in Besprekingsinstellings gestel.',
  'bookings.services.people': 'Mense wat dit doen',
  'bookings.services.peopleHint': 'Net hierdie mense se tye word vir hierdie diens aangebied.',
  'bookings.services.noPeople': 'Voeg eers mense by onder Personeel en ure.',
  'bookings.services.staffMode': 'Wie die kliënt kry',
  'bookings.services.staffMode.any': 'Wie ook al vry is',
  'bookings.services.staffMode.choose': 'Die kliënt kies',
  'bookings.services.staffModeHint': 'Wie ook al vry is, deel besprekings eweredig uit onder die mense wat dit doen.',
  'bookings.services.questions': 'Vrae',
  'bookings.services.questionsHint': 'Word gevra wanneer \'n kliënt bespreek, in die klets en op die besprekingsblad.',
  'bookings.services.questionLabel': 'Vraag',
  'bookings.services.questionKey': 'Gestoor as {{key}}',
  'bookings.services.options': 'Keuses',
  'bookings.services.optionsHint': 'Minstens twee, geskei deur kommas.',
  'bookings.services.addQuestion': 'Voeg \'n vraag by',
  'bookings.services.removeQuestion': 'Verwyder die vraag {{label}}',
  'bookings.services.sortOrder': 'Volgorde',
  'bookings.services.sortOrderHint': 'Laer getalle word eerste gelys.',
  'bookings.services.active': 'Kliënte kan dit bespreek',
  'bookings.services.activeHint': 'Skakel af om nuwe besprekings te stop sonder om dit te verwyder.',
  'bookings.services.error.name': 'Gee die diens \'n naam.',
  'bookings.services.error.duration': 'Die lengte moet minstens 5 minute wees.',
  'bookings.services.error.capacity': '\'n Klas het minstens een plek nodig.',
  'bookings.services.error.link': 'Die skakel moet met http:// of https:// begin.',
  'bookings.services.error.noWindows': 'Voeg minstens een aankomsvenster by.',
  'bookings.services.error.windowTimes': 'Elke venster moet eindig nadat dit begin.',
  'bookings.services.error.windowCapacity': 'Elke venster het plek vir minstens een besoek nodig.',
  'bookings.services.error.questionLabel': 'Elke vraag het sy bewoording nodig.',
  'bookings.services.error.options': '\'n Vraag wat uit \'n lys gekies word, het minstens twee keuses nodig.',
  'bookings.staff.title': 'Personeel en ure',
  'bookings.staff.description': 'Die mense wat kliënte bespreek, wanneer elkeen werk en wanneer hulle weg is.',
  'bookings.staff.new': 'Voeg \'n persoon by',
  'bookings.staff.addFirst': 'Voeg die mense by wat kliënte bespreek',
  'bookings.staff.create': 'Voeg persoon by',
  'bookings.staff.emptyTitle': 'Nog geen mense nie',
  'bookings.staff.emptyHint': 'Voeg elke persoon by wat kliënte kan bespreek, al is dit net jy. Hulle hoef nie aan te meld nie.',
  'bookings.staff.countOfLimit': '{{count}} van {{limit}}',
  'bookings.staff.limitReached': 'Jou plan laat {{limit}} mense toe. Skakel een af of gradeer jou plan op om nog een by te voeg.',
  'bookings.staff.on': 'Bespreekbaar',
  'bookings.staff.off': 'Nie bespreekbaar nie',
  'bookings.staff.nameHint': 'Soos kliënte dit sien, soos Thandi of Dr Naidoo.',
  'bookings.staff.teamMember': 'Spanlid',
  'bookings.staff.noTeamMember': 'Nie \'n spanlid nie',
  'bookings.staff.teamMemberHint': 'Opsioneel. \'n Gekoppelde spanlid word van hul eie besprekings laat weet.',
  'bookings.staff.colour': 'Kleur op die kalender',
  'bookings.staff.hours': 'Werksure',
  'bookings.staff.hours.business': 'Dieselfde as die besigheid',
  'bookings.staff.hours.own': 'Eie ure',
  'bookings.staff.hours.businessHint': 'Hierdie persoon is bespreekbaar wanneer die besigheid oop is.',
  'bookings.staff.hours.businessLink': 'Verander die besigheidsure',
  'bookings.staff.active': 'Kliënte kan hierdie persoon bespreek',
  'bookings.staff.activeHint': 'Skakel af terwyl iemand lank weg is, of as hulle die besigheid verlaat het.',
  'bookings.staff.error.name': 'Voer die persoon se naam in.',
  'bookings.staff.error.email': 'Daardie e-posadres lyk nie reg nie.',
  'bookings.staff.remove.title': 'Verwyder {{name}}?',
  'bookings.staff.remove.body': 'Kliënte kan hierdie persoon nie meer bespreek nie. Besprekings wat reeds gemaak is, bly soos hulle is.',
  'bookings.staff.toast.created': '{{name}} is bygevoeg.',
  'bookings.staff.toast.saved': '{{name}} is gestoor.',
  'bookings.staff.toast.removed': '{{name}} is verwyder.',
  'bookings.timeOff.title': 'Af tyd',
  'bookings.timeOff.hint': 'Verlof, opleiding of enigiets anders wanneer hierdie persoon nie bespreek kan word nie.',
  'bookings.timeOff.empty': 'Geen af tyd wat voorlê nie.',
  'bookings.timeOff.from': 'Van',
  'bookings.timeOff.to': 'Tot',
  'bookings.timeOff.reason': 'Rede',
  'bookings.timeOff.reasonHint': 'Opsioneel. Net jou span sien dit.',
  'bookings.timeOff.add': 'Voeg af tyd by',
  'bookings.timeOff.range': '{{from}} tot {{to}}',
  'bookings.timeOff.removeFor': 'Verwyder die af tyd vanaf {{from}}',
  'bookings.timeOff.error.times': 'Voer in wanneer die af tyd begin en eindig.',
  'bookings.timeOff.error.order': 'Die einde moet na die begin wees.',
  'bookings.timeOff.toast.added': 'Af tyd vir {{name}} bygevoeg.',
  'bookings.classes.title': 'Klasse',
  'bookings.classes.description': 'Sessies van jou klasse en geleenthede, week vir week.',
  'bookings.classes.new': 'Voeg \'n sessie by',
  'bookings.classes.edit': 'Verander die sessie',
  'bookings.classes.create': 'Voeg by',
  'bookings.classes.allClasses': 'Alle klasse',
  'bookings.classes.noClassServices': 'Nog geen klasse of geleenthede nie',
  'bookings.classes.noClassServicesHint': 'Voeg eers \'n diens van die soort Klas of geleentheid by, en gee dit dan hier sessies.',
  'bookings.classes.toServices': 'Gaan na dienste',
  'bookings.classes.emptyWeek': 'Geen sessies hierdie week nie',
  'bookings.classes.emptyWeekHint': 'Voeg een sessie by, of \'n reeks wat elke week herhaal.',
  'bookings.classes.sessionLine': '{{from}} tot {{to}}, {{name}}',
  'bookings.classes.booked': '{{booked}} van {{capacity}} bespreek',
  'bookings.classes.full': 'Vol',
  'bookings.classes.repeats': 'Herhaal',
  'bookings.classes.cancel': 'Kanselleer sessie',
  'bookings.classes.date': 'Datum',
  'bookings.classes.time': 'Begin om',
  'bookings.classes.noPerson': 'Niemand in die besonder nie',
  'bookings.classes.capacityHint': '{{booked}} reeds bespreek.',
  'bookings.classes.repeat': 'Herhaal elke week',
  'bookings.classes.repeatHint': 'Maak \'n sessie op elke gekose dag op hierdie tyd.',
  'bookings.classes.weekdays': 'Dae',
  'bookings.classes.until': 'Tot',
  'bookings.classes.untilHint': 'Die laaste dag van die reeks.',
  'bookings.classes.error.until': 'Die laaste dag moet op of na die eerste wees.',
  'bookings.classes.cancelTitle': 'Kanselleer hierdie sessie van {{name}}?',
  'bookings.classes.cancelBody': 'Die sessie op {{when}} om {{time}} word gekanselleer, en ook sy {{count}} besprekings.',
  'bookings.classes.notifyClients': 'Laat die kliënte weet',
  'bookings.classes.toast.created': '{{count}} sessies bygevoeg.',
  'bookings.classes.toast.saved': 'Die sessie is gestoor.',
  'bookings.classes.toast.cancelled': 'Die sessie van {{name}} is gekanselleer.',
  'bookings.settings.title': 'Besprekingsinstellings',
  'bookings.settings.description': 'Hoe ver vorentoe kliënte mag bespreek, herinneringe, bevestigings en jou openbare besprekingsblad.',
  'bookings.settings.times': 'Besprekingstye',
  'bookings.settings.slotStep': 'Begintye elke',
  'bookings.settings.slotStepHint': 'Hoe ver uitmekaar die afspraaktye is wat aan kliënte aangebied word.',
  'bookings.settings.minNotice': 'Minste kennisgewing, in ure',
  'bookings.settings.minNoticeHint': '\'n Kliënt kan nie \'n tyd gouer as dit bespreek nie.',
  'bookings.settings.horizon': 'Verste vorentoe, in dae',
  'bookings.settings.horizonHint': '\'n Kliënt kan nie verder vorentoe as dit bespreek nie.',
  'bookings.settings.cutoff': 'Veranderinge toegelaat tot, in ure voor',
  'bookings.settings.cutoffHint': 'Binne soveel ure van die begin kan \'n kliënt nie self skuif of kanselleer nie.',
  'bookings.settings.approvalDefault': 'Vra voordat besprekings bevestig word',
  'bookings.settings.approvalDefaultHint': 'Die verstek vir dienste wat op Gebruik die verstek gestel is. Elke diens kan anders sê.',
  'bookings.settings.reminders': 'Herinneringe',
  'bookings.settings.remindersIntro': 'Kliënte kry \'n herinnering met knoppies om te bevestig, te herskeduleer en te kanselleer.',
  'bookings.settings.noReminders': 'Geen herinneringe word gestuur nie.',
  'bookings.settings.hoursBefore': 'Ure voor',
  'bookings.settings.remindersHint': '24 is \'n dag voor. Tot vyf herinneringe.',
  'bookings.settings.addReminder': 'Voeg \'n herinnering by',
  'bookings.settings.removeReminder': 'Verwyder herinnering {{n}}',
  'bookings.settings.confirmations': 'Bevestigings',
  'bookings.settings.confirmationsIntro': 'Hoe kliënte van \'n bespreking, \'n verandering en \'n herinnering laat weet word.',
  'bookings.settings.confirmWhatsapp': 'Per WhatsApp',
  'bookings.settings.confirmWhatsappHint': 'Wanneer die kliënt op WhatsApp bespreek het, of \'n WhatsApp-nommer het.',
  'bookings.settings.confirmEmail': 'Per e-pos',
  'bookings.settings.confirmEmailHint': 'Wanneer daar \'n e-posadres is, met \'n kalenderlêer aangeheg.',
  'bookings.settings.confirmSms': 'Per SMS',
  'bookings.settings.confirmSmsHint': 'Van jou krediete gehef soos enige SMS.',
  'bookings.settings.publicPage': 'Openbare besprekingsblad',
  'bookings.settings.publicPageIntro': '\'n Blad waar enigiemand sonder \'n klets kan bespreek. Deel sy adres op jou webwerf of sosiale media.',
  'bookings.settings.publicEnabled': 'Die besprekingsblad is aan',
  'bookings.settings.slug': 'Kort naam',
  'bookings.settings.slugHint': 'Kleinletters, syfers en koppeltekens, soos smith-dental. Dit is die einde van die blad se adres.',
  'bookings.settings.publicUrl': 'Bladadres',
  'bookings.settings.openPage': 'Maak die blad oop',
  'bookings.settings.publicOffHint': 'Die adres werk sodra die besprekingsblad aan is.',
  'bookings.settings.address': 'Besigheidsadres',
  'bookings.settings.addressHint': 'Gewys op die besprekingsblad en in bevestigings vir dienste by die besigheid.',
  'bookings.settings.prefix': 'Besprekingsnommer begin met',
  'bookings.settings.prefixHint': 'Tot agt letters, syfers, koppeltekens of skuinsstrepe, soos B-. ’n Besprekingsnommer lyk dan soos {{example}}.',
  'bookings.settings.limits': 'Jou plan',
  'bookings.settings.limitStaff': 'Tot {{n}} mense wat kliënte kan bespreek.',
  'bookings.settings.limitStaffNone': 'Geen beperking op mense wat kliënte kan bespreek nie.',
  'bookings.settings.limitServices': 'Tot {{n}} dienste.',
  'bookings.settings.limitServicesNone': 'Geen beperking op dienste nie.',
  'bookings.settings.limitsHint': '\'n Groter plan laat meer toe.',
  'bookings.settings.fixFirst': 'Maak eers die gemerkte velde reg.',
  'bookings.settings.error.reminder': 'Elke herinnering moet meer as 0 en hoogstens 336 uur voor wees.',
  'bookings.settings.error.reminderDuplicate': 'Twee herinneringe is op dieselfde tyd.',
  'bookings.settings.error.slug': 'Gebruik 3 tot 40 kleinletters, syfers en koppeltekens, sonder om met \'n koppelteken te begin of te eindig.',
  'bookings.settings.error.prefix': 'Gebruik tot agt letters, syfers, koppeltekens of skuinsstrepe.',
  'bookings.settings.toast.saved': 'Besprekingsinstellings is gestoor.',
  'bookings.calendar.business': 'Besigheidskalender (elke bespreking)',
  'bookings.calendar.column': 'Kalender',
  'bookings.calendar.none': 'Nie gekoppel nie',
  'bookings.calendar.notYet': 'Nog nie beskikbaar nie',
  'bookings.calendar.notOnPlan': 'Kalendersinkronisasie is nie op jou plan nie. Dit is ingesluit vanaf Plus.',
  'bookings.calendar.connect.google': 'Koppel Google',
  'bookings.calendar.connect.microsoft': 'Koppel Outlook',
  'bookings.calendar.provider.google': 'Google-kalender',
  'bookings.calendar.provider.microsoft': 'Outlook-kalender',
  'bookings.calendar.status.error': 'Moet weer gekoppel word',
  'bookings.calendar.reconnect': 'Koppel weer',
  'bookings.calendar.disconnect': 'Ontkoppel',
  'bookings.calendar.disconnect.title': 'Ontkoppel hierdie kalender?',
  'bookings.calendar.disconnect.body': 'Komende besprekings word uit die kalender gehaal, en sy besige tye keer nie meer besprekings nie. Die besprekings self bly soos hulle is.',
  'bookings.calendar.toast.connected': '{{account}} is gekoppel. Besprekings sal binne \'n minuut daarin verskyn.',
  'bookings.calendar.toast.cancelled': 'Die kalender is nie gekoppel nie.',
  'bookings.calendar.toast.disconnected': 'Die kalender is ontkoppel.',
  'bookings.calendar.error.generic': 'Die kalender kon nie gekoppel word nie. Probeer asseblief weer.',
  'bookings.calendar.error.CALENDAR_SYNC_UNAVAILABLE': 'Kalendersinkronisasie is nog nie beskikbaar nie.',
  'bookings.calendar.error.CALENDAR_PROVIDER_NOT_CONFIGURED': 'Om hierdie soort kalender te koppel is nog nie beskikbaar nie.',
  'bookings.calendar.error.CALENDAR_LINK_EXPIRED': 'Daardie skakel het verval. Koppel asseblief weer.',
  'bookings.calendar.error.CALENDAR_NOT_YOURS': 'Hierdie koppeling is deur iemand anders begin. Koppel asseblief self weer.',
  'bookings.calendar.error.CALENDAR_REFUSED': 'Die kalender het nie toegang toegelaat nie. Koppel asseblief weer en laat toegang toe.',
  'bookings.calendar.error.PLAN_LIMIT_REACHED': 'Kalendersinkronisasie is nie op jou plan nie. Dit is ingesluit vanaf Plus.',
  'bookings.calendar.sync.title': 'Kalendersinkronisasie',
  'bookings.calendar.sync.description': 'Koppel \'n Google- of Outlook-kalender. Besprekings verskyn daarin as afsprake en skuif of verdwyn saam met hulle, en besige tye in \'n persoon se kalender word nie aan kliënte aangebied nie. Afsprake wat in die kalender bygevoeg word, word nooit besprekings nie.',
  'bookings.services.staffMode.none': 'Niemand in die besonder nie (’n span, ’n plek of die besigheid)',
  'bookings.services.staffModeNoneHint': 'Kliënte bespreek ’n tyd binne jou besigheidsure. Die bespreking word nie aan een persoon gegee nie.',
  'bookings.services.atOnce': 'Hoeveel op dieselfde tyd',
  'bookings.services.atOnceHint': 'Hoeveel besprekings van hierdie diens tegelyk kan plaasvind, soos 2 wanneer twee motors saam gewas kan word.',
  'bookings.services.error.atOnce': 'Laat minstens een op ’n slag toe.',
  'bookings.services.noPersonAtOnce': 'Niemand in die besonder nie, {{n}} op ’n slag',
  'bookings.settings.section.times': 'Besprekingstye',
  'bookings.settings.section.reminders': 'Herinneringe',
  'bookings.settings.section.confirmations': 'Bevestigings',
  'bookings.settings.section.page': 'Besprekingsblad',
  'bookings.settings.section.general': 'Algemeen'
};

export default bookings;
